import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronLeft, Landmark, CreditCard, Users, Maximize } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, eur, formatApiError } from "@/lib/api";
import { EASE } from "@/components/motion";
import { useContent } from "@/lib/content";

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (d, n) => {
  const t = new Date(d);
  t.setDate(t.getDate() + n);
  return t.toISOString().slice(0, 10);
};
const nightsBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

const STEPS = ["Reisedaten", "Zimmer", "Gastdaten", "Zahlung"];

export default function BookingPage() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [checkIn, setCheckIn] = useState(params.get("check_in") || today());
  const [checkOut, setCheckOut] = useState(params.get("check_out") || plusDays(today(), 2));
  const [guests, setGuests] = useState(Number(params.get("guests")) || 2);
  const [roomCount, setRoomCount] = useState(1);
  const [roomId, setRoomId] = useState(params.get("room") || "");
  const [form, setForm] = useState({ name: "", email: "", phone: "", street: "", zip: "", city: "", requests: "" });
  const [payment, setPayment] = useState("ueberweisung");
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get("/rooms").then((r) => {
      setRooms(r.data);
      if (!roomId && r.data.length) setRoomId(r.data[0].id);
    }).catch(() => toast.error("Zimmer konnten nicht geladen werden."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nights = Math.max(nightsBetween(checkIn, checkOut), 0);
  const room = rooms.find((r) => r.id === roomId);
  const total = room && nights > 0 ? nights * room.price_per_night * roomCount : 0;

  const canNext = useMemo(() => {
    if (step === 0) return nights >= 1;
    if (step === 1) return !!room;
    if (step === 2) return form.name.trim().length > 1 && /.+@.+\..+/.test(form.email);
    return true;
  }, [step, nights, room, form]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        room_id: roomId,
        check_in: checkIn,
        check_out: checkOut,
        guests,
        rooms_count: roomCount,
        guest_name: form.name,
        guest_email: form.email,
        guest_phone: form.phone,
        guest_street: form.street,
        guest_zip: form.zip,
        guest_city: form.city,
        special_requests: form.requests,
        payment_method: payment,
      });
      if (payment === "stripe") {
        localStorage.setItem("hw_pending_booking", booking.id);
        const { data } = await api.post("/payments/checkout", {
          booking_id: booking.id,
          origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
        return;
      }
      setConfirmed(booking);
      toast.success("Buchung eingegangen — wir melden uns zeitnah!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (confirmed) return <TransferConfirmation booking={confirmed} />;

  return (
    <main className="bg-sand min-h-screen" data-testid="booking-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-32 pb-24">
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-terra font-medium">Direktbuchung</p>
        <h1 className="mt-3 font-serif font-semibold text-4xl sm:text-5xl tracking-tight text-forest">
          Ihr Aufenthalt im Wildpferd
        </h1>

        <ol className="mt-10 flex flex-wrap gap-2 sm:gap-3" data-testid="booking-steps">
          {STEPS.map((s, i) => (
            <li
              key={s}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold border transition-colors duration-300 ${
                i < step
                  ? "bg-forest text-sand border-forest"
                  : i === step
                  ? "bg-white text-forest border-gold"
                  : "bg-transparent text-ink/40 border-forest/10"
              }`}
            >
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${i < step ? "bg-gold text-forest-dark" : "bg-forest/10"}`}>
                {i < step ? <Check size={11} /> : i + 1}
              </span>
              {s}
            </li>
          ))}
        </ol>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 items-start">
          <div className="bg-white rounded-3xl border border-forest/10 p-6 sm:p-10 min-h-[380px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.45, ease: EASE }}
              >
                {step === 0 && (
                  <div data-testid="booking-step-dates">
                    <h2 className="font-serif text-2xl font-semibold text-forest">Wann reisen Sie an?</h2>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <label className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Anreise</span>
                        <input type="date" data-testid="booking-checkin-input" value={checkIn} min={today()}
                          onChange={(e) => { setCheckIn(e.target.value); if (e.target.value >= checkOut) setCheckOut(plusDays(e.target.value, 1)); }}
                          className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm font-semibold outline-none focus:border-gold" />
                      </label>
                      <label className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Abreise</span>
                        <input type="date" data-testid="booking-checkout-input" value={checkOut} min={plusDays(checkIn, 1)}
                          onChange={(e) => setCheckOut(e.target.value)}
                          className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm font-semibold outline-none focus:border-gold" />
                      </label>
                      <label className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Gäste</span>
                        <select data-testid="booking-guests-select" value={guests} onChange={(e) => setGuests(Number(e.target.value))}
                          className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm font-semibold outline-none focus:border-gold">
                          {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} {n === 1 ? "Gast" : "Gäste"}</option>)}
                        </select>
                      </label>
                      <label className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Zimmer</span>
                        <select data-testid="booking-rooms-select" value={roomCount} onChange={(e) => setRoomCount(Number(e.target.value))}
                          className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm font-semibold outline-none focus:border-gold">
                          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} {n === 1 ? "Zimmer" : "Zimmer"}</option>)}
                        </select>
                      </label>
                    </div>
                    {nights >= 1 && (
                      <p className="mt-6 text-sm text-ink/60" data-testid="booking-nights-label">
                        <span className="font-serif text-xl font-semibold text-forest">{nights}</span> {nights === 1 ? "Nacht" : "Nächte"} · {new Date(checkIn).toLocaleDateString("de-DE")} bis {new Date(checkOut).toLocaleDateString("de-DE")}
                      </p>
                    )}
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="booking-step-room">
                    <h2 className="font-serif text-2xl font-semibold text-forest">Welches Zimmer darf es sein?</h2>
                    <div className="mt-8 space-y-4">
                      {rooms.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          data-testid={`booking-room-option-${r.id}`}
                          onClick={() => setRoomId(r.id)}
                          className={`w-full text-left flex gap-5 rounded-2xl border p-4 transition-[border-color,box-shadow] duration-300 ${
                            roomId === r.id ? "border-gold bg-cream/60 shadow-lg shadow-forest/5" : "border-forest/10 hover:border-forest/30"
                          }`}
                        >
                          <img src={r.image_url} alt={r.name} className="h-24 w-32 rounded-xl object-cover shrink-0 hidden sm:block" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-3">
                              <h3 className="font-serif text-xl font-semibold text-forest">{r.name}</h3>
                              <p className="font-serif text-lg font-semibold text-terra">{eur(r.price_per_night)}<span className="text-xs text-ink/45 font-sans font-normal"> / Nacht</span></p>
                            </div>
                            <p className="mt-1 text-xs text-ink/50 flex items-center gap-3">
                              <span className="flex items-center gap-1"><Users size={12} /> {r.capacity} Pers.</span>
                              <span className="flex items-center gap-1"><Maximize size={12} /> {r.size_sqm} m²</span>
                            </p>
                            <p className="mt-2 text-sm text-ink/60 leading-relaxed line-clamp-2">{r.description}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="booking-step-guest">
                    <h2 className="font-serif text-2xl font-semibold text-forest">Wer darf einchecken?</h2>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <label className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Vor- und Nachname *</span>
                        <input data-testid="booking-guest-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Erika Mustermann" className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm outline-none focus:border-gold" />
                      </label>
                      <label className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">E-Mail *</span>
                        <input type="email" data-testid="booking-guest-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="erika@beispiel.de" className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm outline-none focus:border-gold" />
                      </label>
                      <label className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Telefon</span>
                        <input data-testid="booking-guest-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="02594 / 000 000" className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm outline-none focus:border-gold" />
                      </label>
                      <label className="flex flex-col gap-2 sm:col-span-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Straße & Hausnummer (für die Rechnung)</span>
                        <input data-testid="booking-guest-street-input" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })}
                          placeholder="Musterstraße 1" className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm outline-none focus:border-gold" />
                      </label>
                      <label className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">PLZ</span>
                        <input data-testid="booking-guest-zip-input" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })}
                          placeholder="48249" className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm outline-none focus:border-gold" />
                      </label>
                      <label className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Ort</span>
                        <input data-testid="booking-guest-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                          placeholder="Dülmen" className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm outline-none focus:border-gold" />
                      </label>
                      <label className="flex flex-col gap-2 sm:col-span-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Sonderwünsche</span>
                        <textarea data-testid="booking-requests-input" rows={3} value={form.requests} onChange={(e) => setForm({ ...form, requests: e.target.value })}
                          placeholder="Allergie, Babybett, späte Anreise …" className="rounded-xl border border-forest/15 bg-sand/60 px-4 py-3 text-sm outline-none focus:border-gold resize-none" />
                      </label>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="booking-step-payment">
                    <h2 className="font-serif text-2xl font-semibold text-forest">Wie möchten Sie zahlen?</h2>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button type="button" data-testid="payment-option-ueberweisung" onClick={() => setPayment("ueberweisung")}
                        className={`rounded-2xl border p-6 text-left transition-[border-color,box-shadow] duration-300 ${payment === "ueberweisung" ? "border-gold bg-cream/60 shadow-lg shadow-forest/5" : "border-forest/10 hover:border-forest/30"}`}>
                        <Landmark className="text-forest" size={26} />
                        <h3 className="mt-4 font-serif text-xl font-semibold text-forest">Überweisung</h3>
                        <p className="mt-2 text-sm text-ink/60 leading-relaxed">Sie erhalten unsere Bankdaten und zahlen bequem per Vorkasse. Wir reservieren 7 Tage für Sie.</p>
                      </button>
                      <button type="button" data-testid="payment-option-stripe" onClick={() => setPayment("stripe")}
                        className={`rounded-2xl border p-6 text-left transition-[border-color,box-shadow] duration-300 ${payment === "stripe" ? "border-gold bg-cream/60 shadow-lg shadow-forest/5" : "border-forest/10 hover:border-forest/30"}`}>
                        <CreditCard className="text-forest" size={26} />
                        <h3 className="mt-4 font-serif text-xl font-semibold text-forest">Online bezahlen</h3>
                        <p className="mt-2 text-sm text-ink/60 leading-relaxed">Sofort bestätigt: Kreditkarte, PayPal u. a. — sicher abgewickelt über Stripe.</p>
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 flex items-center justify-between">
              <button
                type="button"
                data-testid="booking-back-button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-forest disabled:opacity-0 transition-colors"
              >
                <ChevronLeft size={16} /> Zurück
              </button>
              {step < 3 ? (
                <button
                  type="button"
                  data-testid="booking-next-button"
                  onClick={() => canNext && setStep(step + 1)}
                  disabled={!canNext}
                  className="rounded-full bg-forest text-sand px-8 py-3 text-sm font-semibold hover:bg-forest-dark disabled:opacity-40 transition-colors duration-300"
                >
                  Weiter
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="booking-submit-button"
                  onClick={submit}
                  disabled={busy}
                  className="rounded-full bg-terra text-sand px-8 py-3 text-sm font-bold hover:bg-forest-dark disabled:opacity-50 transition-colors duration-300"
                >
                  {busy
                    ? payment === "stripe"
                      ? "Sichere Bezahlseite wird vorbereitet …"
                      : "Wird gesendet …"
                    : payment === "stripe"
                    ? `Jetzt zahlen · ${eur(total)}`
                    : "Verbindlich buchen"}
                </button>
              )}
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 bg-forest-dark text-sand rounded-3xl p-7 grain relative overflow-hidden" data-testid="booking-summary">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Zusammenfassung</p>
            <h3 className="mt-3 font-serif text-2xl font-semibold">{room ? room.name : "Zimmer wählen"}</h3>
            <dl className="mt-6 space-y-3 text-sm text-sand/80">
              <div className="flex justify-between"><dt>Anreise</dt><dd className="font-semibold text-sand">{new Date(checkIn).toLocaleDateString("de-DE")}</dd></div>
              <div className="flex justify-between"><dt>Abreise</dt><dd className="font-semibold text-sand">{new Date(checkOut).toLocaleDateString("de-DE")}</dd></div>
              <div className="flex justify-between"><dt>Gäste</dt><dd className="font-semibold text-sand">{guests}</dd></div>
              <div className="flex justify-between"><dt>Zimmer</dt><dd className="font-semibold text-sand">{roomCount}</dd></div>
              <div className="flex justify-between"><dt>Nächte</dt><dd className="font-semibold text-sand">{nights > 0 ? nights : "—"}</dd></div>
              <div className="flex justify-between"><dt>Preis / Nacht</dt><dd className="font-semibold text-sand">{room ? eur(room.price_per_night) : "—"}</dd></div>
            </dl>
            <div className="mt-6 pt-5 border-t border-sand/15 flex items-end justify-between">
              <span className="text-sm text-sand/70">Gesamt inkl. MwSt.</span>
              <span className="font-serif text-3xl font-semibold text-gold" data-testid="booking-total-price">{eur(total)}</span>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </main>
  );
}

function TransferConfirmation({ booking }) {
  const c = useContent();
  return (
    <main className="bg-sand min-h-screen" data-testid="booking-confirmation">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-8 pt-36 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="bg-white rounded-3xl border border-gold/40 shadow-2xl shadow-forest/10 p-8 sm:p-12 text-center"
        >
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-forest text-sand"
          >
            <Check size={30} />
          </motion.span>
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold text-forest">Buchung eingegangen!</h1>
          <p className="mt-3 text-ink/60 text-sm sm:text-base leading-relaxed">
            Vielen Dank, {booking.guest_name.split(" ")[0]}. Ihre Reservierung ist bei uns eingegangen.
            Bitte überweisen Sie den Betrag innerhalb von 7 Tagen — danach ist Ihr Zimmer fest bestätigt.
          </p>
          <div className="mt-8 rounded-2xl bg-cream/70 border border-forest/10 p-6 text-left text-sm space-y-2">
            <p className="flex justify-between"><span className="text-ink/50">Buchungsnummer</span><strong className="font-mono" data-testid="confirmation-reference">{booking.reference}</strong></p>
            <p className="flex justify-between"><span className="text-ink/50">Zimmer</span><strong>{booking.rooms_count > 1 ? `${booking.rooms_count} × ` : ""}{booking.room_name}</strong></p>
            <p className="flex justify-between"><span className="text-ink/50">Zeitraum</span><strong>{new Date(booking.check_in).toLocaleDateString("de-DE")} – {new Date(booking.check_out).toLocaleDateString("de-DE")} · {booking.nights} {booking.nights === 1 ? "Nacht" : "Nächte"}</strong></p>
            <p className="flex justify-between text-base pt-2 border-t border-forest/10"><span className="text-ink/50">Betrag</span><strong className="font-serif text-xl text-terra" data-testid="confirmation-total">{eur(booking.total_price)}</strong></p>
          </div>
          <div className="mt-6 rounded-2xl bg-forest-dark text-sand p-6 text-left text-sm space-y-1.5" data-testid="confirmation-bank-details">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-3">Bankverbindung</p>
            <p>Empfänger: {c.bank_recipient}</p>
            <p>IBAN: <span className="font-mono">{c.bank_iban}</span></p>
            {c.bank_bic ? <p>BIC: <span className="font-mono">{c.bank_bic}</span></p> : null}
            <p>Verwendungszweck: <span className="font-mono">{booking.reference}</span></p>
          </div>
          <Link to="/" data-testid="confirmation-home-link" className="mt-8 inline-block rounded-full bg-forest text-sand px-8 py-3 text-sm font-semibold hover:bg-forest-dark transition-colors">
            Zurück zur Startseite
          </Link>
        </motion.div>
      </div>
      <Footer />
    </main>
  );
}
